/**
 * 唯一的公开入口。屏幕只 import 这里，拿到的方法就是全部能力。
 * 它持有两样东西：library（文件派生）与 fold 出来的 states（日志派生），都是可丢弃的缓存，
 * 任何时候 reload 都能从磁盘重建，所以进程里没有不可替代的状态。
 * 唯一同时编排 content、review 与 ports 的模块，不解析 Markdown，不判分，不推进间隔。
 */

import { err, ok, type Issue, type Result, type Score } from './model/result'
import type { Concept } from './model/concept'
import type { AnyQuestion, QuestionAnswer } from './model/question'
import { cardKey } from './model/ids'
import type { CardKey, CardRef, ConceptId, EpochDay, QuestionId, ReviewId } from './model/ids'
import { gradeAnswer } from './review/grading'
import { scoringOf } from './review/question-types'
import type { GradeOutcome, Scoring } from './review/question-types'
import { advance, dueOf, foldReviews, ratingFromScore, reviewedToday as countReviewed, stateFor } from './review/scheduling'
import type { CardState, Rating, Review } from './review/scheduling'
import { EMPTY_LIBRARY, resolveCard } from './content/library'
import type { Library } from './content/library'
import { loadConcepts } from './content/parsing'
import { buildPlan } from './review/plan'
import type { Plan, PlanIntent } from './review/plan'
import { reduceSession } from './review/session'
import type { Receipt, SessionEvent, SessionState } from './review/session'
import { parseSettings } from './ports'
import type { Ports, Settings, SyncReport } from './ports'

/**
 * 提交一次复习。两种形态，编译器挡住混用：
 * auto 给答案，分数与 rating 由系统算；self 给 rating，没有答案可选。
 * `score` 只给自评题带一个预检分（例如关键词命中率），省略时日志里记 null。
 */
export type SubmitInput =
	| { readonly kind: 'auto'; readonly card: CardRef; readonly answer: QuestionAnswer; readonly submissionId: ReviewId }
	| { readonly kind: 'self'; readonly card: CardRef; readonly rating: Rating; readonly score?: Score; readonly submissionId: ReviewId }

export interface SubmitResult {
	/** 知识点卡没有判分结果，它的结论只有档位。 */
	readonly outcome: GradeOutcome | null
	readonly rating: Rating
	readonly nextDueOn: EpochDay
	/** 'again' 时 UI 把这张卡放进本轮队尾。当天再出现一次不算第二次复习，日志里只有一条记录。 */
	readonly requeue: boolean
	/** 幂等重放时为 true，字段全是上次算出来的结果。 */
	readonly replayed: boolean
}

export interface Workspace {
	readonly library: Library
	readonly settings: Settings
	/** git 同步。与 reload 分开：这一步走网络，需要单独重试和进度显示。 */
	pull(): Promise<SyncReport>
	/** 读文件、解析、折叠日志。纯本地、幂等，任何时候重跑收敛到同一状态。 */
	reload(): Promise<void>
	/** 今天或这一轮的视图。now 省略时取时钟的今天，测试传进来就能喂任意日期。 */
	plan(intent: PlanIntent, now?: EpochDay): Plan
	/**
	 * 全流程唯一由业务触发的写盘点。幂等顺序：
	 * 1. submissionId 已在 seenSubmissions → 返回上次结论，replayed = true，不写日志。
	 * 2. card 不在 library → 返回 Issue（条目被删但队列是旧的），不写日志。
	 * 3. kind = auto：gradeAnswer → ratingFromScore；descriptor 声明 scoring = self 的题型走这里被拒。
	 * 4. advance(stateOf(card), rating, today) 得 next。
	 * 5. reviews.append(Review)，然后更新内存 states 与 seenSubmissions。
	 * 6. plan 缓存置空。
	 * 崩溃在第 5 步前：什么都没发生。崩溃在写盘后、返回前：重放同一 submissionId 命中日志，结果一致。
	 */
	submit(input: SubmitInput): Promise<Result<SubmitResult, Issue>>
	/**
	 * 会话推进一步：跑纯 reducer，把交出的 Commit 执行成 submit，再把结果作为 recorded 事件喂回 reducer。
	 * 屏幕因此拿不到 Commit，也碰不到日志，但它仍然不需要知道任何相变规则。
	 */
	stepSession(state: SessionState, event: SessionEvent): Promise<SessionState>
	concept(id: ConceptId): Concept | undefined
	question(id: QuestionId): AnyQuestion | undefined
	/** 缺卡返回 null，这就是「新卡」的表示，没有第二个 phase 标签。 */
	stateOf(ref: CardRef): CardState | null
	issues(): readonly Issue[]
	/** 今日已复习张数，和队列同一个来源，进度条不会有第二份真相。 */
	reviewedToday(): number
	setNewPerDay(n: number): Promise<void>
}

/**
 * 一次提交能不能变成一行日志，以及变成哪一行。结论与档位都在这里算定，
 * 所以 submit 剩下的部分只有写盘和内存视图两件事。
 */
interface Conclusion {
	readonly rating: Rating
	readonly score: Score | null
	readonly outcome: GradeOutcome | null
}

/**
 * 提交拒绝没有文件与行号可定位，能给的只有卡片键。
 * submit.* 码族专用于此，不与格式问题共用码。
 */
function rejectedIssue(code: 'submit.card-missing' | 'submit.kind-mismatch', key: CardKey, message: string): Issue {
	return { card: key, code, message }
}

function conclude(library: Library, input: SubmitInput): Result<Conclusion, Issue> {
	const key = cardKey(input.card)
	const card = resolveCard(library, input.card)
	if (card === null) return err(rejectedIssue('submit.card-missing', key, `仓库里已经没有这张卡：${key}`))
	const scoring: Scoring = card.question === null ? 'self' : scoringOf(card.question.kind)
	if (scoring !== input.kind) {
		return err(rejectedIssue('submit.kind-mismatch', key, `这张卡的 scoring 是 ${scoring}，收不下 kind 为 ${input.kind} 的提交`))
	}
	// 自评题的分数只能是外部带来的预检分，日志里没有判分结果可回放。
	if (input.kind === 'self') return ok({ rating: input.rating, score: input.score ?? null, outcome: null })
	// scoring 与 kind 已相等，而知识点卡恒为 self，所以走到这里的卡一定是题卡。
	const outcome = gradeAnswer(card.question as AnyQuestion, input.answer)
	return ok({ rating: ratingFromScore(outcome.score), score: outcome.score, outcome })
}

/** 装好端口就用。缺哪个端口编译不过，没有可选的默认实现。 */
export async function openWorkspace(ports: Ports): Promise<Workspace> {
	let settings = parseSettings(await ports.settings.read()).settings
	// 设备标识在一次运行里不会变，每张卡都问一次端口就是一次白送的异步调用。
	const device = await ports.device()
	let library: Library = EMPTY_LIBRARY
	let diagnostics: readonly Issue[] = []
	let states = new Map<CardKey, CardState>()
	/** 幂等键到日志行。重放要的档位从这里读，所以进程重启之后仍然认得出老提交。 */
	const logById = new Map<ReviewId, Review>()
	/** 本次进程算出的完整结论。判分结果不进日志，只有这里能还原 outcome。 */
	const results = new Map<ReviewId, SubmitResult>()
	let planCache: { readonly key: string; readonly plan: Plan } | null = null

	async function reload(): Promise<void> {
		const [files, log] = await Promise.all([ports.files.readConceptFiles(), ports.reviews.readAll()])
		const loaded = loadConcepts(files)
		library = loaded.library
		diagnostics = [...loaded.issues, ...log.issues]
		logById.clear()
		for (const review of log.reviews) logById.set(review.id, review)
		states = new Map(foldReviews(log.reviews, library.cardKeys()).states)
		planCache = null
	}

	/** 重放的那份结论：优先用本次进程算出的，只有它不在时才从日志行重建。 */
	function replayOf(id: ReviewId): SubmitResult | null {
		const known = results.get(id)
		if (known !== undefined) return { ...known, replayed: true }
		const review = logById.get(id)
		if (review === undefined) return null
		// 日志里有的卡，状态表里一定有：两者由同一次 fold 产出，submit 也成对写入。
		// 重建给不出 outcome，因为判分结果不属于日志。
		return {
			outcome: null,
			rating: review.rating,
			nextDueOn: dueOf(states.get(review.card) as CardState),
			requeue: review.rating === 'again',
			replayed: true,
		}
	}

	async function submit(input: SubmitInput): Promise<Result<SubmitResult, Issue>> {
		const replay = replayOf(input.submissionId)
		if (replay !== null) return ok(replay)
		const concluded = conclude(library, input)
		if (!concluded.ok) return concluded
		const { rating, score, outcome } = concluded.value
		const today = ports.clock.today()
		const key = cardKey(input.card)
		const next = advance(states.get(key), rating, today)
		const review: Review = { id: input.submissionId, card: key, rating, score, reviewedOn: today, reviewedAtMs: ports.clock.nowMs(), device }
		await ports.reviews.append(review)
		// 写盘成功之后才动内存：append 失败时状态表和日志映射都不变，这张卡还留在队列里，重试只会写一次。
		logById.set(review.id, review)
		states.set(key, next)
		const result: SubmitResult = { outcome, rating, nextDueOn: dueOf(next), requeue: rating === 'again', replayed: false }
		results.set(review.id, result)
		planCache = null
		return ok(result)
	}

	function plan(intent: PlanIntent, now: EpochDay = ports.clock.today()): Plan {
		// 缓存键带上那一天：过了零点，due 的答案就换了。
		const cacheKey = `${now}|${JSON.stringify(intent)}`
		if (planCache === null || planCache.key !== cacheKey) {
			planCache = { key: cacheKey, plan: buildPlan({ library, states, settings }, intent, now) }
		}
		return planCache.plan
	}

	async function setNewPerDay(n: number): Promise<void> {
		settings = { newPerDay: n }
		await ports.settings.write(settings)
		planCache = null
	}

	/** 屏幕拿不到 Commit，也碰不到日志：reducer 交出的那一步由这里执行成 submit，结局再喂回去。 */
	async function stepSession(state: SessionState, event: SessionEvent): Promise<SessionState> {
		const step = reduceSession(state, event)
		if (step.commit.t !== 'submit') return step.state
		const result = await submit(step.commit.input)
		const receipt: Receipt = result.ok ? { kind: 'recorded', result: result.value } : { kind: 'rejected', issues: result.errors }
		return reduceSession(step.state, { t: 'recorded', receipt }).state
	}

	async function pull(): Promise<SyncReport> {
		return ports.pullRepo()
	}

	await reload()

	return {
		// library 与 settings 是闭包变量的只读出口：reload 换 library，setNewPerDay 换 settings。
		get library() {
			return library
		},
		get settings() {
			return settings
		},
		pull,
		reload,
		plan,
		submit,
		stepSession,
		concept: (id) => library.concept(id),
		question: (id) => library.question(id),
		stateOf: (ref) => stateFor(states, ref) ?? null,
		issues: () => diagnostics,
		reviewedToday: () => countReviewed(states, ports.clock.today()),
		setNewPerDay,
	}
}
